import React, { ChangeEvent, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import _omit from 'lodash/omit';
import { ProviderField } from '../../../../../../../models/provider';
import { ContentBody } from '../../../../../../atoms/ContentBody/ContentBody';
import { Button } from '../../../../../../atoms/Button/Button';
import { MaybeChangeMethodButton } from '../../../../../../Templates/MaybeChangeMethodButton/MaybeChangeMethodButton';
import { TemplateElementStyle, TemplateElementType } from '../../../../../../Templates/templates';
import { Footer } from '../../../../../../atoms/Footer/Footer';
import { SubmitProviderInputsPayload } from '../../../../../SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { PageView, UserInteractionEvent } from '../../../../../../../models/analytics';
import { usePageViewEvents } from '../../../../../../../core/Analytics/usePageViewEvents';
import { TitleImageFields } from '../../TitleImageFields';
import { commonMessages } from '../../../../../../atoms/CommonMessages/messages';
import { MultiImageContainer, SelectableImage } from './SelectableImage.styles';

export type SelectableImageViewProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
  allowMultipleSelection?: boolean;
};

export const SelectableImageView = ({
  fields,
  onSubmit,
  allowMultipleSelection = false,
}: SelectableImageViewProps) => {
  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView: allowMultipleSelection
      ? PageView.MULTI_SELECTABLE_IMAGE_TEMPLATE_SCREEN
      : PageView.SINGLE_SELECTABLE_IMAGE_TEMPLATE_SCREEN,
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [selectedAnswers, setSelectedAnswers] = useState<SubmitProviderInputsPayload>({});

  const handleSubmitWithAnalytics = () => {
    sendViewInteractionEvent({ interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION });
    onSubmit(selectedAnswers);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, name, value } = event.target;

    if (checked) {
      setSelectedAnswers((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      setIsButtonDisabled(false);
    } else {
      setSelectedAnswers((prevState) => {
        const answers = _omit(prevState, name);

        if (Object.keys(answers).length === 0) {
          setIsButtonDisabled(true);
        }

        return answers;
      });
    }
  };

  const parsedElements = useMemo(() => {
    const image = fields.find((el) => el.type === TemplateElementType.IMAGE);
    const title = fields.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );
    const selectOptionsField = fields.find((el) => el.selectOptions && el.selectOptions.length > 0);

    return { title, selectOptionsField, image };
  }, [fields]);

  const { title, selectOptionsField, image } = parsedElements;

  return (
    <ContentBody data-testid="single-selectable-image-screen">
      <TitleImageFields title={title} image={image} />

      {selectOptionsField?.selectOptions && (
        <MultiImageContainer role="group" aria-labelledby="image-helptext">
          {selectOptionsField.selectOptions.map(({ value, text, iconUrl }, index) => {
            return (
              <SelectableImage key={value}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor={`image-select-${index}`}>
                  <input
                    type={allowMultipleSelection ? 'checkbox' : 'radio'}
                    id={`image-select-${index}`}
                    value={value}
                    /*
                     * When multiple are allowed, the name of the input needs to be the same
                     * for all, so when switching the choice the other radio unselects itself
                     */
                    name={allowMultipleSelection ? text : selectOptionsField!.name}
                    onChange={onChange}
                    data-testid="answer-input"
                  />
                  <img
                    src={iconUrl}
                    alt={text ?? ''}
                    data-testid="answer-image"
                    draggable="false"
                  />
                </label>
              </SelectableImage>
            );
          })}
        </MultiImageContainer>
      )}

      <Footer>
        <Button
          onClick={handleSubmitWithAnalytics}
          color="primary"
          variant="contained"
          disabled={isButtonDisabled}
          mt="large"
          data-testid="submit-button"
        >
          <FormattedMessage {...commonMessages.continue} />
        </Button>
        <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
      </Footer>
    </ContentBody>
  );
};
